"use client";

import * as React from "react";
import { toast } from "sonner";
import { CreditCard, Pencil, Plus, Trash2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCard, toFa } from "@/lib/format";
import type { BankCard } from "./types";

type Props = { onRefresh: () => void };

type FormState = {
  bankName: string;
  cardNumber: string;
  holderName: string;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  bankName: "",
  cardNumber: "",
  holderName: "",
  sortOrder: "0",
};

export default function BankCardsManager({ onRefresh }: Props) {
  const [cards, setCards] = React.useState<BankCard[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<BankCard | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = React.useState(false);

  const [deleteTarget, setDeleteTarget] = React.useState<BankCard | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/bank-cards", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در دریافت کارت‌ها");
      setCards(data.cards ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت کارت‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  }

  function openEdit(c: BankCard) {
    setEditing(c);
    setForm({
      bankName: c.bankName,
      cardNumber: c.cardNumber,
      holderName: c.holderName,
      sortOrder: String(c.sortOrder),
    });
    setFormOpen(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const digits = form.cardNumber.replace(/\D/g, "");
    if (!form.bankName || digits.length !== 16 || !form.holderName) {
      toast.error("بانک، کارت ۱۶ رقمی و صاحب‌حساب را درست وارد کنید.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        bankName: form.bankName.trim(),
        cardNumber: digits,
        holderName: form.holderName.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      };
      const url = editing
        ? `/api/admin/bank-cards?id=${editing.id}`
        : "/api/admin/bank-cards";
      const res = await fetch(url, {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          editing ? { action: "update", ...payload } : payload,
        ),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "ذخیره ناموفق بود");
      toast.success(editing ? "کارت به‌روزرسانی شد." : "کارت اضافه شد.");
      setFormOpen(false);
      load();
      onRefresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ذخیره ناموفق بود");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(c: BankCard) {
    try {
      const res = await fetch(`/api/admin/bank-cards?id=${c.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا");
      load();
      onRefresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "خطا");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/bank-cards?id=${deleteTarget.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "حذف ناموفق بود");
      toast.success("کارت حذف شد.");
      setDeleteTarget(null);
      load();
      onRefresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "حذف ناموفق بود");
    } finally {
      setDeleting(false);
    }
  }

  const activeCount = cards.filter((c) => c.active).length;

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="size-5 text-primary" />
              کارتهای پرداخت (کارت به کارت)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
                فعال: {activeCount}
              </Badge>
              <Button size="sm" className="gap-1.5" onClick={openCreate}>
                <Plus className="size-4" /> افزودن کارت
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-11 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : cards.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              کارت بانکی ثبت نشده است.
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead>بانک</TableHead>
                    <TableHead>شماره کارت</TableHead>
                    <TableHead>صاحب‌حساب</TableHead>
                    <TableHead>وضعیت</TableHead>
                    <TableHead className="text-center">عملیات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cards.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">
                        {c.bankName}
                      </TableCell>
                      <TableCell dir="ltr" className="font-mono text-xs">
                        {formatCard(c.cardNumber)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {toFa(c.holderName)}
                      </TableCell>
                      <TableCell>
                        {c.active ? (
                          <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
                            فعال
                          </Badge>
                        ) : (
                          <Badge variant="outline">غیرفعال</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => openEdit(c)}
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => toggleActive(c)}
                          >
                            {c.active ? "غیرفعال" : "فعال"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteTarget(c)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "ویرایش کارت" : "افزودن کارت"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="bankName">نام بانک *</Label>
              <Input
                id="bankName"
                placeholder="مثلاً: بانک ملت"
                value={form.bankName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bankName: e.target.value }))
                }
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="cardNumber">شماره کارت (۱۶ رقم) *</Label>
              <Input
                id="cardNumber"
                dir="ltr"
                placeholder="6037-99XX-XXXX-XXXX"
                value={form.cardNumber}
                onChange={(e) =>
                  setForm((f) => ({ ...f, cardNumber: e.target.value }))
                }
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="holderName">صاحب‌حساب *</Label>
              <Input
                id="holderName"
                placeholder="مثلاً: علی محمدی"
                value={form.holderName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, holderName: e.target.value }))
                }
                disabled={submitting}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={submitting}
              >
                انصراف
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "در حال ذخیره..." : "ذخیره"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف کارت</AlertDialogTitle>
            <AlertDialogDescription>
              کارت {deleteTarget?.bankName} (
              {deleteTarget ? formatCard(deleteTarget.cardNumber) : ""})
              حذف خواهد شد.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>انصراف</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
            >
              {deleting ? "در حال حذف..." : "حذف"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
